import React from 'react';
import { useSelector } from 'react-redux';
import { Ad,
  AppContent,
  Author,
  ErrorBoundary,
  Section,
} from 'mm-ui-components';
import { Store } from 'redux';
import { css, StyleSheet } from 'aphrodite/no-important';
import { Theme } from '../../components/connectedComponents/Theme';
import { HeadScripts } from '../../components/connectedComponents/HeadScripts';
import { Page } from '../Page';
import { AppActions, AppState } from '../../store/initialStateRegistration';
import { ErrorReporter } from '../../errorReporting/errorReporter.type';
import { LoadMoreButton, useFetchNextPosts } from '../../components/connectedComponents/LoadMoreButton';
import { useMeasureHydrateTime } from '../useMeasureHydrateTime';
import { getSecondaryHeader } from '../../store/navigations/ampNavigation/secondaryHeader/secondaryHeader.selectors';
import {
  getArticles,
  getBio,
  getImage,
  getJoinDate,
  getJoinDateText,
  getName,
  getShowMorePaginationText,
  getShowMorePaginationURLByKey,
  getSocialButtons,
} from '../../store/template/authorPage/authorPageAntalya/authorPageAntalya.selectors';
import { AuthorPageSectionName } from '../../store/template/authorPage/authorPage.reducer';
import { MetaTags } from '../../components/connectedComponents/MetaTags';
import { NavigationLayout } from '../../components/connectedComponents/NavigationLayout';
import { getAdSlotId, getAdSlotOnSuccess } from '../../store/config/config.selectors';
import { SLOT_NAMES } from '../templates.const';

interface AuthorPageProps {
  store: Store<AppState, AppActions>;
  errorProvider: ErrorReporter;
}

export const AuthorPageAntalyaComponent: React.FunctionComponent<AuthorPageProps> = ({ store, errorProvider }) => {
  useMeasureHydrateTime();
  return (
    <Page store={store} errorProvider={errorProvider}>
      <ErrorBoundary>
        <AuthorPageContentConnected />
      </ErrorBoundary>
    </Page>
  );
};

const createStyle = () => StyleSheet.create({
  sectionsWrapper: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    justifyItems: 'center',
  },
});

export const AuthorPageContentConnected: React.FunctionComponent = () => {
  const image = useSelector(getImage);
  const name = useSelector(getName);
  const socialButtons = useSelector(getSocialButtons);
  const bio = useSelector(getBio);
  const joinDateText = useSelector(getJoinDateText);
  const joinDate = useSelector(getJoinDate);
  const articles = useSelector(getArticles);
  const isSecondaryHeaderEnabled = !!useSelector(getSecondaryHeader);
  const stickyBottomId = useSelector(getAdSlotId(SLOT_NAMES.STICKY_BOTTOM));
  const stickyBottomOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.STICKY_BOTTOM));
  const style = createStyle();

  return (
    <React.Fragment>
      <MetaTags articles={articles} description={bio} title={name} pageImage={null} />
      <HeadScripts />
      <Theme>
        <AppContent>
          <NavigationLayout mainWrapperConfigThemeKey="mainContent">
            <div className={css(style.sectionsWrapper)}>
              <Section>
                <Author {...{
                  image,
                  name,
                  socialButtons,
                  bio,
                  joinDateText,
                  joinDate,
                  articles,
                  isSecondaryHeaderEnabled,
                }}
                />
              </Section>
            </div>
            <LoadMoreAuthorPostsButtonConnected />
          </NavigationLayout>
          <Ad id={stickyBottomId} onSuccess={stickyBottomOnSuccess} isSticky />
        </AppContent>
      </Theme>
    </React.Fragment>
  );
};


export const LoadMoreAuthorPostsButtonConnected: React.FunctionComponent = () => {
  const shouldDisplay = !!useSelector(getShowMorePaginationURLByKey(AuthorPageSectionName));
  const text = useSelector(getShowMorePaginationText);
  const onClick = useFetchNextPosts(AuthorPageSectionName, getShowMorePaginationURLByKey);
  return <LoadMoreButton {...{ onClick, shouldDisplay, text }} />;
};
